import {
  getDataPointHistory,
  getZone,
  listDataPointSources,
  type DataPointSample,
  type DataPointSource,
  type Zone,
} from "@/lib/api/building";

// Loaders are injected so the load logic can be exercised in a test without fetch or a DOM
// (the same pattern as CapabilityList.data). Defaults to the real building API clients.
export interface ZoneDetailLoaders {
  getZone: (buildingId: string, zoneId: string) => Promise<Zone>;
  listDataPointSources: (buildingId: string) => Promise<DataPointSource[]>;
  getDataPointHistory: (
    buildingId: string,
    sourceIds: string[],
    names: string[],
    fromTs?: string,
  ) => Promise<DataPointSample[]>;
}

export interface ZoneDetailResult {
  zone: Zone | null;
  history: DataPointSample[];
}

const HISTORY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

// Load a zone and its live signal history, resolving to a not-found result ({ zone: null,
// history: [] }) on **any** failure rather than rejecting. This is what lets the route render
// its "Vyöhykettä ei löydy." state instead of spinning on 'Ladataan…' forever, and guarantees no
// unhandled rejection escapes to the caller (bug #192).
//
// History is now this zone's own data_point_history samples (source_type "zone", source_id =
// zoneId) rather than the frozen five-channel ZoneSnapshot (#1451) — the KB data_point names are
// discovered from data-points/sources first, mirroring equipment's Signaalit fetch, never a
// hardcoded channel list. zoneSignalSeries (sibling module) groups the result for both the
// route's current-value tiles and its history chart.
export async function loadZoneDetail(
  buildingId: string,
  zoneId: string,
  loaders: ZoneDetailLoaders = {
    getZone,
    listDataPointSources,
    getDataPointHistory,
  },
): Promise<ZoneDetailResult> {
  try {
    const [zone, sources] = await Promise.all([
      loaders.getZone(buildingId, zoneId),
      loaders.listDataPointSources(buildingId),
    ]);
    const zone_source = sources.find(
      (s) => s.source_type === "zone" && s.source_id === zoneId,
    );
    const names = zone_source?.available_names.map((n) => n.name) ?? [];
    if (names.length === 0) return { zone, history: [] };

    const from_ts = new Date(Date.now() - HISTORY_WINDOW_MS).toISOString();
    const history = await loaders.getDataPointHistory(
      buildingId,
      [zoneId],
      names,
      from_ts,
    );
    return { zone, history };
  } catch {
    return { zone: null, history: [] };
  }
}
